import type { MoveView } from "@word-duel/shared";
import { formatDateTime } from "../lib/format";
import { Alert, Modal, Spinner } from "./ui";

function actionName(action: MoveView["actionType"]): string {
  switch (action) {
    case "place":
      return "Played tiles";
    case "pass":
      return "Passed";
    case "exchange":
      return "Exchanged tiles";
    case "resign":
      return "Resigned";
    case "timeout":
      return "Timed out";
    case "final_adjustment":
      return "Final rack adjustment";
  }
}

function signedScore(score: number): string {
  if (score > 0) return `+${score}`;
  if (score < 0) return `−${Math.abs(score)}`;
  return "0";
}

function finalAdjustments(move: MoveView): [number, number] | null {
  if (move.actionType !== "final_adjustment" || !move.payload || typeof move.payload !== "object")
    return null;
  const adjustments = (move.payload as { adjustments?: unknown }).adjustments;
  if (
    !Array.isArray(adjustments) ||
    adjustments.length !== 2 ||
    typeof adjustments[0] !== "number" ||
    typeof adjustments[1] !== "number"
  ) {
    return null;
  }
  return [adjustments[0], adjustments[1]];
}

export function HistoryPanel({
  moves,
  loading,
  error,
  mySeat,
  onClose
}: {
  moves: MoveView[];
  loading: boolean;
  error: string | null;
  mySeat: 1 | 2;
  onClose: () => void;
}) {
  return (
    <Modal
      title="Move history"
      description="Every accepted turn and score change, oldest first."
      onClose={onClose}
      wide
    >
      <div className="history-list" data-testid="history-panel">
        {loading && <Spinner label="Loading moves…" />}
        {error && <Alert tone="error">{error}</Alert>}
        {!loading && !error && moves.length === 0 && (
          <p className="empty-copy">No turns have been played yet.</p>
        )}
        {moves.map((move) => {
          const adjustments = finalAdjustments(move);
          return (
            <article className="history-item" key={`${move.turnNumber}-${move.actionType}`}>
              <span className="history-item__turn">
                {move.turnNumber.toString().padStart(2, "0")}
              </span>
              <div className="history-item__body">
                <div>
                  <strong>{move.actingSeat === mySeat ? "You" : "Opponent"}</strong>
                  <span>{actionName(move.actionType)}</span>
                </div>
                {move.words.length > 0 && (
                  <ul className="word-list" aria-label="Words scored">
                    {move.words.map((word, index) => (
                      <li key={`${word.word}-${index}`}>
                        <b>{word.word}</b> <span>+{word.score}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {adjustments && (
                  <p className="history-adjustments">
                    P1 {signedScore(adjustments[0])} / P2 {signedScore(adjustments[1])}
                  </p>
                )}
                <small>
                  {formatDateTime(move.createdAt)} · score {move.resultingScores[0]}—
                  {move.resultingScores[1]}
                </small>
              </div>
              <span className={`history-item__score ${move.scoreDelta > 0 ? "is-positive" : ""}`}>
                {signedScore(move.scoreDelta)}
              </span>
            </article>
          );
        })}
      </div>
    </Modal>
  );
}
